import { Component } from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ICareTeam } from 'src/app/interfaces/CareTeam/ICareTeam';
import { CareTeamService } from 'src/app/services/careTeam.service';
import { CareTeamExpandComponent } from './care-team-expand/care-team-expand.component';
import { TranslateService } from '@ngx-translate/core';
import { SwalToastService } from 'src/app/services/swal.service';
import { PermissionService } from 'src/app/services/permission.service';
import { EpisodeOfCareService } from 'src/app/services/episodeOfCare.service';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-current-care-team',
  templateUrl: './current-care-team.component.html',
  styleUrls: ['./current-care-team.component.css'],
  standalone: false,
})
export class CurrentCareTeamComponent {
  public expandComp = CareTeamExpandComponent;

  private destroy$ = new Subject<void>();

  careTeams?: ICareTeam[];
  isLoading: boolean = false;

  careTeamCustomActions: Array<{
    iconType: string;
    tooltip: string;
    handler: Function;
    className?: string;
    isVisible?: Function;
  }> = [];

  constructor(
    private readonly careTeamService: CareTeamService,
    private readonly translate: TranslateService,
    private readonly swalToastService: SwalToastService,
    private readonly permissionService: PermissionService,
    private readonly eocService: EpisodeOfCareService,
    private readonly router: Router,
  ) {}

  ngOnInit() {
    this.careTeamService.careTeamLoadingSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: boolean) => {
        this.isLoading = data;
      });

    this.careTeamService.careTeamActiveDataSub
      .pipe(takeUntil(this.destroy$))
      .subscribe((data: ICareTeam[]) => {
        this.careTeams = data;
      });

    this.careTeamService.getData();

    this.careTeamCustomActions = [
      {
        iconType: 'icons:form',
        tooltip: 'Edit End Date',
        handler: (item: ICareTeam) => this.openModalFun(item),
        isVisible: (item: ICareTeam) =>
          !!item.isSubmitted &&
          !!item.isActive &&
          this.hasPermission(['a-ct']) &&
          this.isEocOrInpatientContext(),
      },
      {
        iconType: 'eye',
        tooltip: 'View',
        handler: (item: ICareTeam) => this.openViewFun(item),
        isVisible: (item: ICareTeam) =>
          !!item.isSubmitted &&
          !!item.isActive &&
          !this.isEocOrInpatientContext(),
      },
    ];
  }

  isEocOrInpatientContext(): boolean {
    const hasEoc = !(
      this.eocService.currentEocId() === null ||
      this.eocService.currentEocId() === ''
    );

    return (
      hasEoc &&
      (this.router.url.includes('episodeOfCare') ||
        this.router.url.includes('inPatientView'))
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  hasPermission(permission: string[]): boolean {
    return this.permissionService.can(permission);
  }

  openModalFun(item?: ICareTeam) {
    this.careTeamService.careTeamReadOnlySub.next(false);
    this.careTeamService.careTeamEditSub.next(item);
    this.careTeamService.careTeamModalSub.next(true);
  }

  openViewFun(item: ICareTeam) {
    this.careTeamService.careTeamReadOnlySub.next(true);
    this.careTeamService.careTeamEditSub.next(item);
    this.careTeamService.careTeamModalSub.next(true);
  }

  deleteFun(careTeamId: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'deletePlaceholder2',
      )} ${this.translate.instant('Care Team')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.careTeamService.deleteCareTeam(careTeamId.toString()).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Care Team deleted successfully',
            );
            this.careTeamService.getData();
          },
          error: () => {
            this.swalToastService.toastError('Unable to delete Care Team');
          },
        });
      }
    });
  }

  stopFun(careTeamId: number) {
    Swal.fire({
      text: `${this.translate.instant(
        'unassignPlaceholder2',
      )} ${this.translate.instant('Care Team')}?`,
      showDenyButton: false,
      showCancelButton: true,
      cancelButtonText: `${this.translate.instant('Cancel')}`,
      confirmButtonText: `${this.translate.instant('Ok')}`,
    }).then((result) => {
      if (result.isConfirmed) {
        this.careTeamService.unassignCareTeam(careTeamId.toString()).subscribe({
          next: () => {
            this.swalToastService.toastSuccess(
              'Care Team unassigned successfully',
            );
            this.careTeamService.getData();
          },
          error: () => {
            this.swalToastService.toastError('Unable to unassign Care Team');
          },
        });
      }
    });
  }
}
